import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { type FC, useState } from "react";
import { ACCOUNT_LIMIT_KEYS } from "../../generic/server/supabase-account-service";
import type { AccountSectionProps } from "./account-general-section";

const labelFor = (key: string): string =>
  key
    .split("_")
    .map((x) => x[0]!.toUpperCase() + x.slice(1))
    .join(" ");

const ENTRIES = Object.entries(ACCOUNT_LIMIT_KEYS) as [
  keyof typeof ACCOUNT_LIMIT_KEYS,
  (typeof ACCOUNT_LIMIT_KEYS)[keyof typeof ACCOUNT_LIMIT_KEYS],
][];

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  gap: 2,
} as const;

export const AccountLimitsSection: FC<AccountSectionProps> = ({ account, onSave }) => {
  const [limits, setLimits] = useState<{ [key: string]: number }>(account.limits ?? {});
  const [saving, setSaving] = useState(false);

  const setLimit = (key: string, value: number): void =>
    setLimits((prev) => ({ ...prev, [key]: value }));

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await onSave({ limits });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper id="limits" elevation={24} sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Limits &amp; Features
      </Typography>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Limits
          </Typography>
          <Box sx={GRID_SX}>
            {ENTRIES.filter(([, def]) => def.type === "number").map(([key]) => (
              <TextField
                key={key}
                label={labelFor(key)}
                type="number"
                variant="standard"
                value={limits[key] ?? 0}
                onChange={(e) => setLimit(key, Number(e.target.value))}
              />
            ))}
          </Box>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Feature Toggles
          </Typography>
          <Box sx={GRID_SX}>
            {ENTRIES.filter(([, def]) => def.type === "boolean").map(([key]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={Boolean(limits[key])}
                    onChange={(e) => setLimit(key, +e.target.checked)}
                  />
                }
                label={labelFor(key)}
              />
            ))}
          </Box>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Entitlements
          </Typography>
          <Box sx={GRID_SX}>
            {ENTRIES.flatMap(([key, def]) =>
              def.type === "select"
                ? [
                    <FormControl key={key} variant="standard">
                      <InputLabel id={`${key}-label`}>{labelFor(key)}</InputLabel>
                      <Select
                        labelId={`${key}-label`}
                        label={labelFor(key)}
                        value={(def.values as readonly string[])[limits[key] ?? 0]}
                        onChange={(e) =>
                          setLimit(key, (def.values as readonly string[]).indexOf(e.target.value))
                        }
                      >
                        {(def.values as readonly string[]).map((v) => (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>,
                  ]
                : [],
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            data-analytics-id="ce-admin-save-limits"
            variant="contained"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};
